import { useCallback } from 'react';
import { CodeResponse, useGoogleLogin } from '@react-oauth/google';
import { useHistory } from 'react-router-dom';
import { ExternalAuthenticationProvider, LoginWithAuthProviderDoc } from '@minecraft.graphql-operations';
import { useMutation } from '@apollo/client';
import { setAccessToken, setRefreshToken, redirectByRole, getAuthorizationErrorMessage } from '@blocs.auth';
import { gqlSystemRoleCode } from '@minecraft.graphql-types';
import { captureExceptionEvent } from '@blocs.sentry';
import { useTranslation } from '@blocs.i18n';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { ERROR_CODES } from '../../utils/loginHelpers';

export const useLogInWithGoogle = () => {
  const router = useHistory();
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const [loginWithAuthProviderMutation, { loading: isLoggingIn }] = useMutation(LoginWithAuthProviderDoc);

  const onGoogleSignInFailure = useCallback(
    (errorResponse: Pick<CodeResponse, 'error' | 'error_description' | 'error_uri'>): void => {
      switch (errorResponse.error) {
        case ERROR_CODES.ACCESS_DENIED:
          showMessage({ color: 'danger', message: t('login:login.error.googleAuthenticationFailed.accessDenied') });
          break;
        case ERROR_CODES.TEMPORARILY_UNAVAILABLE:
          showMessage({
            color: 'danger',
            message: t('login:login.error.googleAuthenticationFailed.temporarilyUnavailable'),
          });
          break;
        default:
          showMessage({ color: 'danger', message: t('login:login.error.googleAuthenticationFailed.generic') });
          break;
      }
    },
    []
  );

  const handleGoogleSignInErrors = (errorToHandle) => {
    const errorCode = errorToHandle?.graphQLErrors?.[0]?.data?.errorCode;
    switch (errorCode) {
      case 5: {
        const message = getAuthorizationErrorMessage(t, errorToHandle);
        showMessage({ color: 'danger', message });
        break;
      }
      case 9:
        showMessage({ color: 'danger', message: t('common:globalError.accountNotFoundErrorEmailOnlyAuth') });
        break;
      default:
        showMessage({ color: 'danger', message: t('login:login.error.googleAuthenticationFailed.generic') });
    }
  };

  const onGoogleSignInSuccess = useCallback(
    async (code: string) => {
      try {
        const variables = {
          authCode: code,
          authProvider: ExternalAuthenticationProvider.Google,
        };

        const res = await loginWithAuthProviderMutation({
          variables,
        });

        if (res?.errors || !res?.data?.loginWithAuthProvider?.access || !res?.data?.loginWithAuthProvider?.refresh) {
          throw res?.errors || new Error('failed to login with google auth provider');
        }

        const { access, refresh } = res.data.loginWithAuthProvider;

        setAccessToken(access);
        setRefreshToken(refresh);
        redirectByRole({
          router,
          selectedAudience: gqlSystemRoleCode.TALENT,
          token: { access, refresh },
        });
      } catch (error) {
        handleGoogleSignInErrors(error);
        captureExceptionEvent(error);
      }
    },
    [router]
  );

  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => onGoogleSignInSuccess(codeResponse.code),
    onError: (errorResponse) => onGoogleSignInFailure(errorResponse),
    flow: 'auth-code',
  });

  return {
    googleLogin,
    isLoggingIn,
  };
};
